import { apiFetch } from './http';
import { memberBookingRefusal } from './member';

// ─────────────────────────────────────────────────────────────────────
// A MEMBER BOOKS EQUIPMENT ON ITS OWN (docs/domains/ASSET_BOOKING.md, slice 2).
//
// `POST /member/asset-bookings` — a ball machine for an hour, with no court.
// The sibling of `createMemberBooking`, and shaped the same way: the member
// comes from the session, the price and the payment status are the server's,
// and a refusal is an ordinary answer carried as a CODE for the screen to
// translate. Equipment WITH a court is `assetIds` on the court intent instead.
// ─────────────────────────────────────────────────────────────────────

export interface MemberAssetBookingIntent {
  assetId: string;
  date: string;
  startTime: string;
  durationMin: number;
  /** Idempotency key, derived from the intent — see `memberBookingRequestId`. */
  requestId?: string;
}

/** The key a repeat of THIS equipment booking would produce. Stable, not a secret. */
export function memberAssetBookingRequestId(intent: MemberAssetBookingIntent): string {
  return ['asset', intent.assetId, intent.date, intent.startTime, intent.durationMin]
    .map((p) => String(p ?? ''))
    .join(':');
}

export interface MemberAssetBookingResult {
  success: boolean;
  /** The row as the server created it, `mine: true`, with its price and status. */
  assetBooking?: Record<string, unknown>;
  /** How long the equipment is held, as the SERVER measured it (#607). */
  expiresInMs?: number;
  /** A sentence for the generic refusals; the equipment codes travel as `code`. */
  error?: string;
  code?: string;
}

/**
 * Book a piece of equipment as the signed-in member.
 *
 * Never throws for a refusal — a taken machine is an ordinary answer the screen
 * shows. Transport failures do throw, because «the request never happened» is a
 * different thing from «the club said no».
 */
export async function createMemberAssetBooking(
  intent: MemberAssetBookingIntent,
): Promise<MemberAssetBookingResult> {
  const res = await apiFetch('/member/asset-bookings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // BUILT FIELD BY FIELD, not spread — the same discipline as the court intent:
    // this is the only place that decides what a member sends.
    body: JSON.stringify({
      assetId: intent.assetId,
      date: intent.date,
      startTime: intent.startTime,
      durationMin: intent.durationMin,
      requestId: intent.requestId ?? memberAssetBookingRequestId(intent),
    }),
  });
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok) {
    return {
      success: true,
      assetBooking: (body?.assetBooking as Record<string, unknown>) ?? undefined,
      ...(typeof body?.expiresInMs === 'number' ? { expiresInMs: body.expiresInMs } : {}),
    };
  }
  const code = String(body?.error ?? '');
  return {
    success: false,
    code,
    error: memberBookingRefusal(code, body?.reason == null ? null : String(body.reason)),
  };
}
